import { z } from "zod";
import {
  ERP_SYNC_CONFIG_PROPERTY_ORDER,
  ERPSyncConfigSchema,
  OpportunityCustomFieldMappingSchema,
} from "./schemas";

/**
 * Per-customer Epicor sync configuration. Stored as `integrationConfig` jsonb on
 * the `erpIntegrationSyncsTable` row. Extends the shared {@link ERPSyncConfigSchema}
 * with the Epicor company/plant selection, the Epicor-specific costing and
 * supplier defaults, and the custom field mappings.
 */
export const EpicorSyncConfigSchema = ERPSyncConfigSchema.extend({
  overwriteQuoteOnRevision: z.boolean().default(true).meta({
    title: "Quote revisions overwrite the Epicor quote",
    description:
      "Later revisions of a Forgepoint quote rewrite the Epicor quote created for it instead of creating a new Epicor quote",
  }),
  enableIntegrationLookup: z.boolean().default(false).meta({
    title: "Agent read access to Epicor",
    description:
      "Let the agent look up records in Epicor, such as recent sales-order prices, with read-only requests",
  }),
  company: z
    .string()
    .min(1)
    .meta({ title: "Company", description: "Epicor company code" }),
  plant: z
    .string()
    .min(1)
    .meta({ title: "Plant", description: "Epicor plant code" }),
  useStandardCostForRawMaterial: z.boolean().default(true).meta({
    title: "Use standard cost for raw material",
    description:
      'Value raw material at Epicor standard cost, matching "Use Std. Cost for Raw Mtl" on the Method Tracker report',
  }),
  defaultSubcontractSupplierId: z
    .string()
    .min(1)
    .max(8)
    .default("EXTERNAL")
    .meta({
      title: "Default subcontract supplier",
      description:
        "Epicor supplier ID assigned to subcontract operations whose Epicor operation has no default supplier",
    }),
  defaultCustomerGroupCode: z.string().min(1).max(6).meta({
    title: "Default customer group",
    description:
      "Epicor customer group assigned to customers created by a company push",
  }),
  itemCustomFieldMapping: OpportunityCustomFieldMappingSchema.meta({
    title: "Item custom field mapping",
    description: "Map Forgepoint item custom fields to Epicor part fields",
  }),
  quoteCustomFieldMapping: OpportunityCustomFieldMappingSchema.meta({
    title: "Quote custom field mapping",
    description: "Map Forgepoint quote custom fields to Epicor quote fields",
  }),
  quoteLineCustomFieldMapping: OpportunityCustomFieldMappingSchema.meta({
    title: "Quote line custom field mapping",
    description:
      "Map Forgepoint quote line custom fields to Epicor quote detail fields",
  }),
});

export type EpicorSyncConfig = z.infer<typeof EpicorSyncConfigSchema>;

export const EPICOR_SYNC_CONFIG_PROPERTY_ORDER = [
  ...ERP_SYNC_CONFIG_PROPERTY_ORDER,
  "overwriteQuoteOnRevision",
  "enableIntegrationLookup",
  "company",
  "plant",
  "useStandardCostForRawMaterial",
  "defaultSubcontractSupplierId",
  "defaultCustomerGroupCode",
  "itemCustomFieldMapping",
  "quoteCustomFieldMapping",
  "quoteLineCustomFieldMapping",
];
